/**
 * AdminResumePage Component
 *
 * Status: [IMPLEMENTED]
 * Task: T2.8.1 - Create admin route
 * Task: T2.11.3 - Edit Modal UI for Raw Content Editor
 * Task: T2.11.4 - Frontend Regenerate Flow
 *
 * Main admin page for generating resumes at /azzmin-access.
 */

import React, { useState, useCallback } from 'react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { generateResume, regenerateResume } from '../services/adminApi';
import JobPostingForm from '../components/admin/JobPostingForm';
import ProgressIndicator from '../components/admin/ProgressIndicator';
import ChangeSummary from '../components/admin/ChangeSummary';
import PDFDownload from '../components/admin/PDFDownload';

/**
 * F100.1: `inShell` renders the page inside the AdminShell tab (no standalone
 * header/footer/background). Default false keeps the original standalone page.
 */
const AdminResumePage = ({ inShell = false }) => {
  const { logout, isLoading: authLoading } = useAdminAuth({ requireAuth: true });
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Edit modal state (T2.11.3)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedJson, setEditedJson] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [rawContent, setRawContent] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Store original form data for regeneration (T2.11.4)
  const [lastJobPosting, setLastJobPosting] = useState('');
  const [lastCompanyName, setLastCompanyName] = useState('');

  const handleGenerate = useCallback(async ({ companyName, jobPosting }) => {
    setIsGenerating(true);
    setError(null);
    setResult(null);
    setRawContent(null);

    // Store form data for potential regeneration (T2.11.4)
    setLastCompanyName(companyName);
    setLastJobPosting(jobPosting);

    const response = await generateResume({ companyName, jobPosting });

    if (response.success) {
      setResult(response.data);
      // Store raw content for edit modal (T2.11.4)
      setRawContent(response.data.rawContent || null);
    } else {
      if (response.unauthorized) {
        // Auth hook will handle redirect
        return;
      }
      setError(response.error);
    }

    setIsGenerating(false);
  }, []);

  const handleReset = useCallback(() => {
    setResult(null);
    setError(null);
    setRawContent(null);
    setShowEditModal(false);
    setEditedJson('');
    setJsonError('');
    setLastJobPosting('');
    setLastCompanyName('');
  }, []);

  // Edit modal handlers (T2.11.3)
  const handleOpenEdit = useCallback(() => {
    setEditedJson(JSON.stringify(rawContent, null, 2));
    setJsonError('');
    setShowEditModal(true);
  }, [rawContent]);

  const handleCloseEdit = useCallback(() => {
    setShowEditModal(false);
    setJsonError('');
  }, []);

  const handleRegenerate = useCallback(async () => {
    // Parse and validate JSON
    let parsed;
    try {
      parsed = JSON.parse(editedJson);
    } catch (e) {
      setJsonError(`Invalid JSON: ${e.message}`);
      return;
    }

    // Validate we have the required data for regeneration
    if (!lastJobPosting || !lastCompanyName) {
      setJsonError('Missing original job posting data. Please generate a new resume first.');
      return;
    }

    setJsonError('');
    setIsRegenerating(true);

    try {
      const response = await regenerateResume({
        companyName: lastCompanyName,
        jobPosting: lastJobPosting,
        editedContent: parsed,
      });

      if (response.success) {
        // Update result with new PDF
        setResult(response.data);

        // Update raw content for future edits
        setRawContent(response.data.rawContent || parsed);

        // Close modal on success
        setShowEditModal(false);
      } else {
        if (response.unauthorized) {
          // Auth hook will handle redirect
          return;
        }
        setJsonError(response.error || 'Regeneration failed');
      }
    } catch (e) {
      setJsonError(`Error: ${e.message}`);
    } finally {
      setIsRegenerating(false);
    }
  }, [editedJson, lastJobPosting, lastCompanyName]);

  // Show loading while auth is checking
  if (authLoading) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-neutral-600 border-t-sky-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={inShell ? '' : 'min-h-screen bg-neutral-900'}>
      {/* Header (standalone mode only - the shell provides its own) */}
      {!inShell && (
      <header className="bg-neutral-800 border-b border-neutral-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-neutral-100">
            Resume Generator
          </h1>
          <button
            onClick={logout}
            className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700 rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </header>
      )}

      {/* Main content */}
      <main className={inShell ? '' : 'max-w-4xl mx-auto px-4 py-8'}>
        {/* Show form if not generating and no result */}
        {!isGenerating && !result && (
          <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-6 md:p-8">
            <JobPostingForm
              onSubmit={handleGenerate}
              disabled={isGenerating}
            />

            {/* Error message */}
            {error && (
              <div className="mt-6 p-4 bg-red-900/30 border border-red-700 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-red-300">Generation Failed</h4>
                    <p className="mt-1 text-sm text-red-400">{error}</p>
                  </div>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="mt-4 px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-neutral-200 text-sm rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        )}

        {/* Show progress indicator while generating */}
        {isGenerating && (
          <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-6 md:p-8">
            <ProgressIndicator message="Generating your tailored resume..." />
          </div>
        )}

        {/* Show results after generation */}
        {result && (
          <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-6 md:p-8">
            {/* Success header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-900/30 rounded-full mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-neutral-100">
                Resume Generated Successfully
              </h2>
              <p className="mt-2 text-sm text-neutral-400">
                Your tailored resume is ready for download
              </p>
            </div>

            {/* Change summary */}
            <ChangeSummary
              changes={result.changes}
              generatedAt={result.generatedAt}
              jobTitle={result.jobTitle}
            />

            {/* Download and Edit buttons */}
            <div className="flex flex-col items-center gap-4">
              <PDFDownload
                pdfBase64={result.pdfBase64}
                filename={result.filename}
                changesMd={result.changesMd}
              />

              {/* Edit Content button (T2.11.3) */}
              {rawContent && (
                <button
                  onClick={handleOpenEdit}
                  className="flex items-center gap-2 px-6 py-3 bg-neutral-700 hover:bg-neutral-600 text-neutral-200 font-medium rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>Edit Content</span>
                </button>
              )}
            </div>

            {/* Generate another button */}
            <div className="mt-8 text-center border-t border-neutral-700 pt-6">
              <button
                onClick={handleReset}
                className="px-6 py-2 bg-neutral-700 hover:bg-neutral-600 text-neutral-200 rounded-lg transition-colors"
              >
                Generate Another Resume
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Footer (standalone mode only) */}
      {!inShell && (
      <footer className="py-6">
        <p className="text-center text-xs text-neutral-600">
          Private Admin Interface
        </p>
      </footer>
      )}

      {/* Edit Content Modal (T2.11.3, T2.11.4) */}
      {showEditModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={isRegenerating ? undefined : handleCloseEdit}
        >
          <div
            className="bg-neutral-800 rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border border-neutral-700"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-neutral-700">
              <h2 className="text-xl font-semibold text-neutral-100">
                Edit Resume Content
              </h2>
              <button
                onClick={handleCloseEdit}
                disabled={isRegenerating}
                className={`p-2 rounded-lg transition-colors
                  ${isRegenerating
                    ? 'text-neutral-600 cursor-not-allowed'
                    : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700'
                  }`}
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 flex-1 overflow-hidden flex flex-col min-h-0">
              <p className="text-sm text-neutral-400 mb-4">
                Edit the JSON below and click Regenerate to create a new PDF with your changes.
              </p>

              {/* JSON Error */}
              {jsonError && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg">
                  <p className="text-sm text-red-400">{jsonError}</p>
                </div>
              )}

              {/* JSON Editor Textarea */}
              <textarea
                className="flex-1 min-h-[400px] w-full bg-neutral-900 text-neutral-200 font-mono text-sm p-4 rounded-lg border border-neutral-600 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none resize-none"
                value={editedJson}
                onChange={e => {
                  setEditedJson(e.target.value);
                  setJsonError('');
                }}
                spellCheck={false}
                placeholder="JSON content will appear here..."
              />
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-700">
              <button
                onClick={handleCloseEdit}
                disabled={isRegenerating}
                className={`px-5 py-2.5 rounded-lg transition-colors
                  ${isRegenerating
                    ? 'text-neutral-500 cursor-not-allowed'
                    : 'text-neutral-300 hover:text-neutral-100 hover:bg-neutral-700'
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                disabled={isRegenerating}
                className={`px-5 py-2.5 font-medium rounded-lg transition-colors flex items-center gap-2
                  ${isRegenerating
                    ? 'bg-sky-700 cursor-not-allowed opacity-75'
                    : 'bg-sky-600 hover:bg-sky-700'
                  } text-white`}
              >
                {isRegenerating && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isRegenerating ? 'Regenerating...' : 'Regenerate PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResumePage;
